/**
 * Analytics Feature Builder
 * Factory function to wire infrastructure -> application layer
 * This is where dependency injection happens
 * 
 * Wires:
 * - Event tracker adapters (infrastructure)
 * - Analytics service (application)
 */

import { CompositeEventTracker } from '../infra/adapters/composite.adapter';
import { WebenageAdapter } from '../infra/adapters/webenage.adapter';
import { GoogleAnalyticsAdapter } from '../infra/adapters/googleAnalytics.adapter';
import { GoKwikAdapter } from '../infra/adapters/gokwik.adapter';
import { EventLoggerAdapter } from '../infra/adapters/eventLogger.adapter';
import { setFacebookServerLogger } from '../utils/facebookCommonAttributes';
import type { EventTracker } from '../domain/interfaces/EventTracker';
import { createAnalyticsService } from '../application/services/AnalyticsService';
import type { AnalyticsService } from '../application/services/AnalyticsService';

/**
 * Analytics Provider Configuration
 */
export interface AnalyticsProviderConfig {
  webenage?: {
    licenseCode: string;
    enabled?: boolean;
  };
  googleAnalytics?: {
    measurementId: string;
    enabled?: boolean;
  };
  goKwik?: {
    enabled?: boolean;
    trackAllEvents?: boolean;
  };
  eventLogger?: {
    enabled?: boolean;
    baseUrl?: string;
  };
  custom?: EventTracker[];
}

/**
 * Analytics Build Result
 */
export interface AnalyticsBuildResult {
  service: AnalyticsService;
  tracker: EventTracker;
}

/**
 * Build Analytics Feature
 * Creates and wires all analytics providers and services
 * 
 * @param config - Configuration for analytics providers
 * @returns Analytics service and tracker instance
 */
export async function analyticsBuild(
  config: AnalyticsProviderConfig = {}
): Promise<AnalyticsBuildResult> {
  const compositeTracker = new CompositeEventTracker();

  // Add WebEngage tracker if configured
  if (config.webenage?.enabled !== false && config.webenage?.licenseCode) {
    const webenageTracker = new WebenageAdapter({
      licenseCode: config.webenage.licenseCode,
      enabled: config.webenage.enabled,
    });
    await webenageTracker.initialize?.();
    compositeTracker.addTracker(webenageTracker, 'webengage');
  }

  // Add Google Analytics tracker if configured
  if (config.googleAnalytics?.enabled !== false && config.googleAnalytics?.measurementId) {
    const gaTracker = new GoogleAnalyticsAdapter({
      measurementId: config.googleAnalytics.measurementId,
      enabled: config.googleAnalytics.enabled,
    });
    await gaTracker.initialize?.();
    compositeTracker.addTracker(gaTracker, 'ga');
  }

  // Add GoKwik tracker if configured
  if (config.goKwik?.enabled === true) {
    const goKwikTracker = new GoKwikAdapter({
      enabled: config.goKwik.enabled,
      trackAllEvents: config.goKwik.trackAllEvents,
    });
    await goKwikTracker.initialize?.();
    compositeTracker.addTracker(goKwikTracker, 'gokwik');
  }

  // Add EventLogger server tracker if configured
  let eventLoggerTracker: EventLoggerAdapter | null = null;
  if (config.eventLogger?.enabled !== false) {
    eventLoggerTracker = new EventLoggerAdapter({
      enabled: config.eventLogger?.enabled,
      baseUrl: config.eventLogger?.baseUrl,
    });
    await eventLoggerTracker.initialize?.();
    compositeTracker.addTracker(eventLoggerTracker, 'eventlogger');
  }

  setFacebookServerLogger(
    eventLoggerTracker
      ? (payload) => eventLoggerTracker.logEventLogger(payload)
      : null,
  );

  // Add custom trackers if provided
  // Note: Custom trackers default to 'ga' event provider type, but you can extend this
  // to support custom event provider types if needed
  if (config.custom) {
    config.custom.forEach((tracker) => {
      compositeTracker.addTracker(tracker, 'ga'); // Default to 'ga' for custom trackers
    });
  }

  // Initialize composite tracker
  await compositeTracker.initialize();

  // Create analytics service
  const service = createAnalyticsService(compositeTracker);

  return {
    service,
    tracker: compositeTracker,
  };
}

/**
 * Create Analytics Service (convenience function)
 * Creates analytics service with default configuration from environment variables.
 * Returns the same singleton instance on every call so all hooks and modules
 * share one initialized tracker (avoids duplicate SDK init + race conditions).
 *
 * @returns Analytics service instance
 */
let _analyticsServicePromise: Promise<AnalyticsService> | null = null;

export async function createAnalytics(): Promise<AnalyticsService> {
  if (_analyticsServicePromise) {
    return _analyticsServicePromise;
  }

  const config: AnalyticsProviderConfig = {};

  // Load configuration from environment variables
  // Vite automatically loads .env.local for dev and .env.production for prod
  if (import.meta.env.VITE_WEBENAGE_LICENSE_CODE) {
    config.webenage = {
      licenseCode: import.meta.env.VITE_WEBENAGE_LICENSE_CODE,
      enabled: import.meta.env.VITE_WEBENAGE_ENABLED !== 'false',
    };
  }

  if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
    config.googleAnalytics = {
      measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID,
      enabled: import.meta.env.VITE_GA_ENABLED !== 'false',
    };
  }

  config.goKwik = {
    enabled: import.meta.env.VITE_GO_KWIK_ENABLED === 'true',
    trackAllEvents: import.meta.env.VITE_GO_KWIK_ALL_EVENTS === 'true',
  };

  const eventLoggerUrl = import.meta.env.VITE_EVENT_LOGGER_URL?.trim();
  config.eventLogger = {
    enabled: import.meta.env.VITE_EVENT_LOGGER_ENABLED !== 'false',
    ...(eventLoggerUrl ? { baseUrl: eventLoggerUrl } : {}),
  };

  _analyticsServicePromise = analyticsBuild(config).then(({ service }) => service);
  return _analyticsServicePromise;
}

